// ==========================================================================
// STUDIO — Sticky image: outgoing stays fixed, incoming slides in on top
// ==========================================================================

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

export function initStudio() {
const sections = document.querySelectorAll('.studio__section[data-index]');
  const images   = document.querySelectorAll('.studio__sticky-image');
  const navLinks        = document.querySelectorAll('.nav__contextual-left a');
  const topbarLinks     = document.querySelectorAll('.mobile-topbar__inner--scroll a');

  // Mobile: switch topbar from title to nav links when bottom of first section hits top
  const mobTopbarTitle   = document.querySelector('.studio-topbar--title');
  const mobTopbarLinks   = document.querySelector('.studio-topbar--links');
  const firstSection     = document.querySelector('.studio__section');

  if (mobTopbarTitle && mobTopbarLinks && firstSection) {
    new IntersectionObserver(([entry]) => {
      const pastSection = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      mobTopbarTitle.style.display = pastSection ? 'none' : '';
      mobTopbarLinks.style.display = pastSection ? 'flex' : 'none';
    }, { threshold: 0 }).observe(firstSection);
  }

  if (!sections.length || !images.length) return;

  // Initial state — first image visible at rest, others parked offscreen
  images.forEach((img, i) => {
    gsap.set(img, { yPercent: i === 0 ? 0 : 100, zIndex: i === 0 ? 1 : 0 });
  });

  // Build nav/topbar link map: section id → [<a>, ...] (multiple instances possible)
  const navLinkMap    = {};
  const topbarLinkMap = {};
  navLinks.forEach(link => {
    const id = link.getAttribute('href')?.replace('#', '');
    if (id) (navLinkMap[id] ??= []).push(link);
  });
  topbarLinks.forEach(link => {
    const id = link.getAttribute('href')?.replace('#', '');
    if (id) (topbarLinkMap[id] ??= []).push(link);
  });

  let currentIndex = 0;

  // Nav + topbar highlight — value-N → our-values, founder-bio → our-team
  const highlightNav = (sectionId) => {
    const navId = sectionId.startsWith('value-') ? 'our-values'
               : sectionId === 'founder-bio'    ? 'our-team'
               : sectionId;
    navLinks.forEach(link => link.classList.remove('is-active'));
    navLinkMap[navId]?.forEach(link => link.classList.add('is-active'));
    topbarLinks.forEach(link => link.classList.remove('is-active'));
    topbarLinkMap[navId]?.forEach(link => link.classList.add('is-active'));
  };

  const setActive = (newIndex, direction, sectionId) => {
    if (newIndex === currentIndex) return;

    const outgoing = document.querySelector(`.studio__sticky-image[data-index="${currentIndex}"]`);
    const incoming = document.querySelector(`.studio__sticky-image[data-index="${newIndex}"]`);

    if (!incoming) return;

    // Kill all tweens, snap non-participants to their parked positions
    images.forEach(img => {
      gsap.killTweensOf(img);
      const idx = parseInt(img.dataset.index, 10);
      if (img !== outgoing && img !== incoming) {
        gsap.set(img, { yPercent: idx < newIndex ? -100 : 100, zIndex: 0 });
      }
    });

    // Outgoing stays at 0 — completely still
    if (outgoing) gsap.set(outgoing, { yPercent: 0, zIndex: 1 });

    // Incoming slides in from below (scroll down) or above (scroll up), on top
    const inFromY = direction > 0 ? 100 : -100;
    gsap.set(incoming, { yPercent: inFromY, zIndex: 2 });
    gsap.to(incoming, {
      yPercent: 0,
      duration: 0.8,
      ease: 'power2.inOut',
      onComplete: () => {
        // Once covered, park the outgoing offscreen
        if (outgoing) gsap.set(outgoing, { yPercent: direction > 0 ? -100 : 100, zIndex: 0 });
      },
    });

    currentIndex = newIndex;

    // Keep is-active for mobile (CSS shows/hides via display)
    images.forEach(img => img.classList.remove('is-active'));
    incoming.classList.add('is-active');

    highlightNav(sectionId);
  };

  // #about — first section has no data-index (no sticky image), track nav only
  const aboutSection = document.querySelector('#about');
  if (aboutSection) {
    ScrollTrigger.create({
      trigger: aboutSection,
      start: 'top 60%',
      onEnter:     () => highlightNav('about'),
      onEnterBack: () => highlightNav('about'),
    });
  }

  // Map index → section id so onLeaveBack can restore the previous image
  // (index 0 = #about, which has no data-index)
  const idByIndex = { 0: 'about' };
  sections.forEach(s => { idByIndex[parseInt(s.dataset.index, 10)] = s.id; });

  sections.forEach((section) => {
    const index = parseInt(section.dataset.index, 10);
    const id    = section.id;

    ScrollTrigger.create({
      trigger: section,
      start: 'top 60%',
      onEnter:     () => setActive(index,  1, id),
      onEnterBack: () => setActive(index, -1, id),
      // Scrolling back above this section's start → previous section's image
      onLeaveBack: () => setActive(index - 1, -1, idByIndex[index - 1]),
    });
  });
}
